import React from "react";
import { View, Text, Modal, TouchableOpacity, StyleSheet, TouchableWithoutFeedback } from "react-native";
import Animated, {
  FadeInDown,
  FadeInUp,
  ZoomIn,
  ZoomOut,
  FadeIn,
  FadeOut
} from "react-native-reanimated";
import { LinearGradient } from "expo-linear-gradient";
import { Feather } from "@expo/vector-icons";
import { widthPercentageToDP as wp, heightPercentageToDP as hp } from "react-native-responsive-screen";

export interface CustomAlertButton {
  text: string;
  onPress?: () => void;
  style?: "default" | "cancel" | "destructive";
}

interface CustomAlertProps {
  visible: boolean;
  title: string;
  message?: string;
  buttons: CustomAlertButton[];
  onDismiss?: () => void;
  icon?: keyof typeof Feather.glyphMap;
  iconColor?: string;
}

const CustomAlert: React.FC<CustomAlertProps> = ({
  visible,
  title,
  message,
  buttons,
  onDismiss,
  icon = "info",
  iconColor = "#0596D0",
}) => {
  if (!visible) return null;

  return (
    <Modal
      transparent
      visible={visible}
      animationType="none"
      onRequestClose={onDismiss}
    >
      <TouchableWithoutFeedback onPress={onDismiss}>
        <View className="flex-1 justify-center items-center bg-black/50 px-6">
          <TouchableWithoutFeedback>
            <Animated.View 
              entering={ZoomIn.duration(400).springify().damping(15)}
              className="bg-white rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl elevation-10"
            >
              <View className="p-6 items-center">
                {/* Icon Header */}
                <View className="w-16 h-16 rounded-full bg-blue-50 justify-center items-center mb-4">
                  <Feather name={icon} size={32} color={iconColor} />
                </View>

                {/* Text Content */}
                <Text className="text-xl font-bold text-gray-800 text-center mb-2" style={{ fontFamily: "kawkabBold" }}>
                  {title}
                </Text>
                {message && (
                  <Text className="text-sm text-gray-500 text-center mb-6 leading-5" style={{ fontFamily: "kawkabReg" }}>
                    {message}
                  </Text>
                )}

                {/* Buttons Container */}
                <View className="w-full flex-col space-y-3 mt-2 gap-3">
                  {buttons.map((btn, index) => {
                    const isCancel = btn.style === "cancel";
                    const isDestructive = btn.style === "destructive";

                    if (isDestructive) {
                      return (
                        <TouchableOpacity
                          key={index}
                          onPress={btn.onPress}
                          className="w-full py-3.5 rounded-xl border border-red-500 bg-red-50"
                        >
                          <Text className="text-red-600 text-center font-bold text-base">
                            {btn.text}
                          </Text>
                        </TouchableOpacity>
                      );
                    }

                    if (isCancel) {
                      return (
                        <TouchableOpacity
                          key={index}
                          onPress={btn.onPress}
                          className="w-full py-3.5 rounded-xl bg-gray-100"
                        >
                          <Text className="text-gray-600 text-center font-bold text-base">
                            {btn.text}
                          </Text>
                        </TouchableOpacity>
                      );
                    }

                    // Default Style (Primary)
                    return (
                      <TouchableOpacity
                        key={index}
                        onPress={btn.onPress}
                        className="w-full rounded-xl overflow-hidden"
                      >
                        <LinearGradient
                          colors={["#004E6D", "#0596D0"]}
                          start={{ x: 0, y: 0 }}
                          end={{ x: 1, y: 1 }}
                          className="py-3.5 w-full items-center"
                        >
                          <Text className="text-white text-center font-bold text-base">
                            {btn.text}
                          </Text>
                        </LinearGradient>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </View>
            </Animated.View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
};

export default CustomAlert;
